import React, { useState, useCallback } from 'react'
import {
  CreditCard,
  Eye,
  EyeOff,
  Shield,
  ToggleLeft,
  ToggleRight,
  AlertTriangle,
  CheckCircle2,
  XCircle
} from 'lucide-react'

// --- Admin brand colors (per-file constant, same pattern as other super-admin components) ---

const ADMIN_BRAND = {
  primary: '#4F46E5',
  primaryLight: '#EEF2FF',
  primaryText: '#3730A3',
  gradient: 'linear-gradient(to right, #2563EB, #4F46E5)',
}

// --- Types ---

export interface PaymentConfigFormState {
  isEnabled: boolean
  gatewayType: string
  publishableKey: string
  secretKey: string
  testMode: boolean
  stripeAccountId: string
}

export interface PaymentConfigEditorProps {
  config: PaymentConfigFormState
  onChange: (config: PaymentConfigFormState) => void
  modeWarning?: string | null
  disabled?: boolean
}

export const DEFAULT_PAYMENT_CONFIG: PaymentConfigFormState = {
  isEnabled: false,
  gatewayType: 'stripe_connect',
  publishableKey: '',
  secretKey: '',
  testMode: true,
  stripeAccountId: '',
}

// --- Validation helpers ---

function isValidPublishableKey(key: string): boolean {
  return key.startsWith('pk_test_') || key.startsWith('pk_live_')
}

function isValidSecretKeyFormat(key: string): boolean {
  if (!key || key.includes('***')) return false // masked value
  return key.startsWith('sk_test_') || key.startsWith('sk_live_')
}

// --- Component ---

const PaymentConfigEditor: React.FC<PaymentConfigEditorProps> = ({
  config,
  onChange,
  modeWarning,
  disabled = false,
}) => {
  const [showSecret, setShowSecret] = useState(false)

  const handleChange = useCallback(
    (field: keyof PaymentConfigFormState, value: string | boolean) => {
      onChange({ ...config, [field]: value })
    },
    [config, onChange]
  )

  const pkValid = config.publishableKey.length > 0 && isValidPublishableKey(config.publishableKey)
  const pkInvalid = config.publishableKey.length > 0 && !isValidPublishableKey(config.publishableKey)
  const skIsMasked = config.secretKey.includes('***')
  const skValid = config.secretKey.length > 0 && (skIsMasked || isValidSecretKeyFormat(config.secretKey))

  return (
    <div className="space-y-6">
      {/* Enable / Disable Toggle */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div>
          <p className="text-sm font-medium text-gray-900">Payment Processing</p>
          <p className="text-xs text-gray-500">
            {config.isEnabled ? 'Accepting payments via Stripe' : 'Payment processing is disabled'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => handleChange('isEnabled', !config.isEnabled)}
          disabled={disabled}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundColor: config.isEnabled ? '#DEF7EC' : '#FDE8E8',
            color: config.isEnabled ? '#03543F' : '#9B1C1C',
          }}
        >
          {config.isEnabled ? (
            <>
              <ToggleRight className="w-5 h-5" />
              Enabled
            </>
          ) : (
            <>
              <ToggleLeft className="w-5 h-5" />
              Disabled
            </>
          )}
        </button>
      </div>

      {/* Test / Live Mode Toggle */}
      <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
        <div>
          <p className="text-sm font-medium text-gray-900">Environment Mode</p>
          <p className="text-xs text-gray-500">
            {config.testMode
              ? 'Using Stripe test keys -- no real charges'
              : 'Using Stripe live keys -- real payments'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => handleChange('testMode', true)}
            disabled={disabled}
            className={`px-3 py-1.5 rounded-l-lg text-xs font-semibold border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              config.testMode
                ? 'bg-orange-100 text-orange-800 border-orange-300'
                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
            }`}
          >
            Test Mode
          </button>
          <button
            type="button"
            onClick={() => handleChange('testMode', false)}
            disabled={disabled}
            className={`px-3 py-1.5 rounded-r-lg text-xs font-semibold border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              !config.testMode
                ? 'bg-green-100 text-green-800 border-green-300'
                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
            }`}
          >
            Live Mode
          </button>
        </div>
      </div>

      {/* Mode Mismatch Warning */}
      {modeWarning && (
        <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-yellow-800">Key Mode Mismatch</p>
            <p className="text-xs text-yellow-700 mt-0.5">{modeWarning}</p>
          </div>
        </div>
      )}

      {/* Stripe Keys Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="w-5 h-5 text-gray-600" />
          <h3 className="text-sm font-semibold text-gray-900">Stripe API Keys</h3>
        </div>

        <div className="space-y-4">
          {/* Publishable Key */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Publishable Key
            </label>
            <div className="relative">
              <input
                type="text"
                value={config.publishableKey}
                onChange={(e) => handleChange('publishableKey', e.target.value)}
                placeholder="pk_test_... or pk_live_..."
                disabled={disabled}
                className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 disabled:bg-gray-50 disabled:cursor-not-allowed"
              />
              {pkValid && (
                <CheckCircle2 className="absolute right-2.5 top-2.5 w-4 h-4 text-green-500" />
              )}
              {pkInvalid && (
                <XCircle className="absolute right-2.5 top-2.5 w-4 h-4 text-red-400" />
              )}
            </div>
            {pkInvalid && (
              <p className="text-xs text-red-500 mt-1">Must start with pk_test_ or pk_live_</p>
            )}
          </div>

          {/* Secret Key */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Secret Key
            </label>
            <div className="relative">
              <input
                type={showSecret ? 'text' : 'password'}
                value={config.secretKey}
                onChange={(e) => handleChange('secretKey', e.target.value)}
                placeholder="sk_test_... or sk_live_..."
                disabled={disabled}
                className="w-full px-3 py-2 pr-16 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 disabled:bg-gray-50 disabled:cursor-not-allowed"
              />
              <div className="absolute right-2.5 top-2 flex items-center gap-1.5">
                {skValid && (
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                )}
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  tabIndex={-1}
                >
                  {showSecret ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
            {skIsMasked && (
              <p className="text-xs text-gray-500 mt-1">
                Showing masked value. Enter a new key to replace it.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Stripe Connect Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-gray-600" />
          <h3 className="text-sm font-semibold text-gray-900">Stripe Connect</h3>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Connected Account ID
          </label>
          <input
            type="text"
            value={config.stripeAccountId}
            onChange={(e) => handleChange('stripeAccountId', e.target.value)}
            placeholder="acct_..."
            disabled={disabled}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 disabled:bg-gray-50 disabled:cursor-not-allowed"
          />
          <p className="text-xs text-gray-500 mt-1">
            Optional. Enter the Stripe Connect account ID (starts with acct_) for platform payments.
          </p>
        </div>
      </div>
    </div>
  )
}

export default PaymentConfigEditor
